import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { notificationService } from '@/api/services/notification';
import { NOTIFICATION_DEFAULT_PAGE_SIZE } from '@/config/constants';
import {
  buildUnreadGroupChatCountByGroupId,
  filterCommentNotifications,
  filterGroupChatNotifications,
} from '@/lib/commentNotificationUtils';
import { useUser } from '@/hooks/queries/auth';
import type { QueryKey, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
  UserNotification,
  UserNotificationDetails,
  UserNotificationRequest,
} from '@/types/notifications';

const NOTIFICATION_POLL_INTERVAL_MS = 10 * 1000;

type NotificationQueryResult = {
  status: boolean;
  data?: UserNotificationDetails;
  error?: { code: string; message: string };
};

/**
 * Query key for user notifications
 * @param params - User notification request parameters
 * @returns Query key
 */
export const useUserNotificationsQueryKey = (params: UserNotificationRequest) =>
  ['notifications', params.pageNo, params.pageSize, params.status ?? 'all'] as QueryKey;

export const useUnreadNotificationsQueryKey = () =>
  ['notifications', 'unread'] as QueryKey;

const unreadNotificationsQueryOptions = {
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    notificationService.getAllUserNotifications({
      pageNo: 1,
      pageSize: NOTIFICATION_DEFAULT_PAGE_SIZE,
      status: 'unread',
    }, signal),
  refetchInterval: NOTIFICATION_POLL_INTERVAL_MS,
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: true,
} as const;

/**
 * Custom hook to fetch user notifications
 * @param params - User notification request parameters
 * @param options - Query options
 * @returns User notification details
 */
export const useUserNotifications = (
  params: UserNotificationRequest,
  options?: UseQueryOptions<NotificationQueryResult, Error, NotificationQueryResult, QueryKey>
) => {
  const isAuthenticated = useUser((state) => Boolean(state.token && state.user));

  return useQuery({
    queryKey: useUserNotificationsQueryKey(params),
    queryFn: ({ signal }) => notificationService.getAllUserNotifications(params, signal),
    refetchInterval: NOTIFICATION_POLL_INTERVAL_MS,
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
    ...options,
  });
};

/**
 * Shared unread notifications poll used by sidebar badge and tab alerts.
 */
export const useUnreadNotifications = (
  options?: UseQueryOptions<NotificationQueryResult, Error, NotificationQueryResult, QueryKey>
) => {
  const isAuthenticated = useUser((state) => Boolean(state.token && state.user));

  return useQuery({
    queryKey: useUnreadNotificationsQueryKey(),
    enabled: isAuthenticated,
    ...unreadNotificationsQueryOptions,
    ...options,
  });
};

/**
 * Unread comment-related notifications derived from the shared unread poll.
 */
export const useUnreadCommentNotifications = (
  options?: UseQueryOptions<NotificationQueryResult, Error, UserNotification[], QueryKey>
) => {
  const isAuthenticated = useUser((state) => Boolean(state.token && state.user));

  return useQuery({
    queryKey: useUnreadNotificationsQueryKey(),
    enabled: isAuthenticated,
    ...unreadNotificationsQueryOptions,
    select: (response) => filterCommentNotifications(response.data?.notifications ?? []),
    ...options,
  });
};

/**
 * Per-group unread group-chat counts derived from the shared unread notifications poll.
 */
export const useUnreadGroupChatCountsByGroupId = (
  options?: UseQueryOptions<NotificationQueryResult, Error, Record<string, number>, QueryKey>
) => {
  const isAuthenticated = useUser((state) => Boolean(state.token && state.user));

  return useQuery({
    queryKey: useUnreadNotificationsQueryKey(),
    enabled: isAuthenticated,
    ...unreadNotificationsQueryOptions,
    select: (response) =>
      buildUnreadGroupChatCountByGroupId(response.data?.notifications ?? []),
    ...options,
  });
};

/**
 * Unread group-chat notification count for a single group.
 */
export const useUnreadGroupChatCountForGroup = (groupId: string | undefined) => {
  const { data: countsByGroupId } = useUnreadGroupChatCountsByGroupId();

  if (!groupId) {
    return 0;
  }

  return countsByGroupId?.[String(groupId)] ?? 0;
};

/**
 * Collects unread group-chat notification ids for a group from cached unread poll data.
 */
export function getUnreadGroupChatNotificationIdsForGroup(
  notifications: UserNotification[],
  groupId: string | number
): number[] {
  const normalizedGroupId = Number(groupId);

  return filterGroupChatNotifications(notifications)
    .filter((notification) => Number(notification.groupId) === normalizedGroupId)
    .map((notification) => notification.id);
}

/**
 * Marks all cached unread group-chat notifications for a group as read.
 */
export function useMarkGroupChatNotificationsAsReadForGroup() {
  const queryClient = useQueryClient();
  const markAsReadMutation = useMarkNotificationsAsRead();

  return useCallback(
    (groupId: string) => {
      const cached = queryClient.getQueryData<NotificationQueryResult>(
        useUnreadNotificationsQueryKey()
      );
      const notificationIds = getUnreadGroupChatNotificationIdsForGroup(
        cached?.data?.notifications ?? [],
        groupId
      );

      if (notificationIds.length === 0) {
        return;
      }

      markAsReadMutation.mutate({
        notificationIds,
        groupId: Number(groupId),
      });
    },
    [markAsReadMutation, queryClient]
  );
}

/**
 * Custom hook to mark notifications as read
 * @param options - Mutation options
 * @returns Mutation options
 */
export const useMarkNotificationsAsRead = (
  options?: UseMutationOptions<
    { status: boolean; data?: { code: number | string; message: string }; error?: { code: string; message: string } },
    Error,
    { notificationIds: Array<string | number>; groupId: number }
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationService.markNotificationsAsRead,
    ...options,
    onMutate: async ({ notificationIds }) => {
      const unreadQueryKey = useUnreadNotificationsQueryKey();
      await queryClient.cancelQueries({ queryKey: unreadQueryKey });

      const previousUnread = queryClient.getQueryData<NotificationQueryResult>(unreadQueryKey);
      const normalizedIds = new Set(notificationIds.map((id) => Number(id)));

      queryClient.setQueryData<NotificationQueryResult>(unreadQueryKey, (current) => {
        if (!current?.data) {
          return current;
        }

        const remainingNotifications = (current.data.notifications ?? []).filter(
          (notification) => !normalizedIds.has(notification.id),
        );
        const removedUnreadCount = (current.data.notifications ?? []).filter((notification) =>
          normalizedIds.has(notification.id),
        ).length;

        return {
          ...current,
          data: {
            ...current.data,
            notifications: remainingNotifications,
            unreadNotificationCount: {
              ...current.data.unreadNotificationCount,
              unreadCount: Math.max(
                0,
                (current.data.unreadNotificationCount?.unreadCount ?? 0) -
                  removedUnreadCount,
              ),
            },
          },
        };
      });

      return { previousUnread };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUnread) {
        queryClient.setQueryData(useUnreadNotificationsQueryKey(), context.previousUnread);
      }
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: useUnreadNotificationsQueryKey(),
      });
      options?.onSuccess?.(...args);
    },
  });
};
